import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { Shell } from './layout/shell';
import { AuthService, authGuard, guestGuard, permissionGuard } from '@rind/auth';
import { InventoryPermissions } from './core/api/models';

export const routes: Routes = [
  {
    path: 'login',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./features/login/login-page').then((m) => m.LoginPage),
  },
  {
    path: 'login/totp',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./features/login/totp-challenge-page').then((m) => m.TotpChallengePage),
  },
  {
    path: '',
    component: Shell,
    canActivate: [authGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: () => {
          const auth = inject(AuthService);
          if (auth.needsTotpEnrollment()) {
            return 'security/totp';
          }
          if (auth.can(InventoryPermissions.SkusRead)) {
            return 'skus';
          }
          if (auth.can(InventoryPermissions.DeliveriesRead)) {
            return 'deliveries';
          }
          if (
            auth.can(InventoryPermissions.ProductsRead) ||
            auth.can(InventoryPermissions.ContainersRead) ||
            auth.can(InventoryPermissions.ProductContainersRead)
          ) {
            return 'products';
          }
          if (auth.can(InventoryPermissions.TradePartnersRead)) {
            return 'trade-partners';
          }
          if (auth.can(InventoryPermissions.OperatorsRead)) {
            return 'people';
          }
          return 'forbidden';
        },
      },
      {
        path: 'forbidden',
        loadComponent: () =>
          import('./features/login/forbidden-page').then((m) => m.ForbiddenPage),
      },
      {
        path: 'security/totp',
        loadComponent: () =>
          import('./features/security/totp-setup-page').then((m) => m.TotpSetupPage),
      },
      {
        path: 'products',
        loadComponent: () =>
          import('./features/products/products-shell').then((m) => m.ProductsShell),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: () => {
              const auth = inject(AuthService);
              if (auth.can(InventoryPermissions.ProductsRead)) {
                return 'list';
              }
              if (auth.can(InventoryPermissions.ContainersRead)) {
                return 'containers';
              }
              return 'list';
            },
          },
          {
            path: 'list',
            canActivate: [permissionGuard],
            data: { permission: InventoryPermissions.ProductsRead },
            loadComponent: () =>
              import('./features/products/product-workspace').then((m) => m.ProductWorkspace),
            children: [
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () =>
                  import('./features/products/product-select-prompt').then(
                    (m) => m.ProductSelectPrompt,
                  ),
              },
              {
                path: 'new',
                redirectTo: '/products/list',
                pathMatch: 'full',
              },
              {
                path: ':productId',
                canActivate: [permissionGuard],
                data: { permission: InventoryPermissions.ProductsRead },
                loadComponent: () =>
                  import('./features/products/product-detail-shell').then(
                    (m) => m.ProductDetailShell,
                  ),
                children: [
                  {
                    path: '',
                    pathMatch: 'full',
                    redirectTo: () => {
                      const auth = inject(AuthService);
                      return auth.can(InventoryPermissions.ProductContainersRead)
                        ? 'containers'
                        : 'split-rules';
                    },
                  },
                  // Legacy: product info lives in the right-hand editor panel
                  {
                    path: 'info',
                    redirectTo: '',
                    pathMatch: 'full',
                  },
                  {
                    path: 'containers',
                    canActivate: [permissionGuard],
                    data: { permission: InventoryPermissions.ProductContainersRead },
                    loadComponent: () =>
                      import('./features/products/product-containers').then(
                        (m) => m.ProductContainersPage,
                      ),
                  },
                  {
                    path: 'split-rules',
                    loadComponent: () =>
                      import('./features/products/product-split-rules').then(
                        (m) => m.ProductSplitRulesPage,
                      ),
                  },
                ],
              },
            ],
          },
          {
            path: 'containers',
            canActivate: [permissionGuard],
            data: { permission: InventoryPermissions.ContainersRead },
            loadComponent: () =>
              import('./features/containers/container-list').then((m) => m.ContainerListPage),
          },
          {
            path: 'containers/new',
            redirectTo: '/products/containers',
            pathMatch: 'full',
          },
          {
            path: 'containers/:containerId',
            canActivate: [permissionGuard],
            data: { permission: InventoryPermissions.ContainersRead },
            loadComponent: () =>
              import('./features/containers/container-detail').then((m) => m.ContainerDetailPage),
          },
          // Legacy: packaging is managed on each product's Containers tab
          {
            path: 'product-containers',
            redirectTo: '/products/list',
            pathMatch: 'full',
          },
          {
            path: 'product-containers/new',
            redirectTo: '/products/list',
            pathMatch: 'full',
          },
          // Legacy: /products/:productId → /products/list/:productId
          {
            path: ':productId',
            redirectTo: ({ params }) => `list/${params['productId']}`,
          },
        ],
      },
      {
        path: 'containers',
        pathMatch: 'full',
        redirectTo: 'products/containers',
      },
      {
        path: 'containers/:containerId',
        redirectTo: ({ params }) => `products/containers/${params['containerId']}`,
      },
      {
        path: 'product-containers',
        pathMatch: 'full',
        redirectTo: 'products/list',
      },
      {
        path: 'product-containers/new',
        pathMatch: 'full',
        redirectTo: 'products/list',
      },
      {
        path: 'skus',
        canActivate: [permissionGuard],
        data: { permission: InventoryPermissions.SkusRead },
        loadComponent: () => import('./features/skus/sku-list').then((m) => m.SkuListPage),
      },
      {
        path: 'skus/:skuCode',
        canActivate: [permissionGuard],
        data: { permission: InventoryPermissions.SkusRead },
        loadComponent: () => import('./features/skus/sku-detail').then((m) => m.SkuDetailPage),
      },
      {
        path: 'reports/skus-printed',
        canActivate: [permissionGuard],
        data: { permission: InventoryPermissions.SkusRead },
        loadComponent: () =>
          import('./features/reports/skus-printed-report').then((m) => m.SkusPrintedReportPage),
      },
      {
        path: 'deliveries',
        canActivate: [permissionGuard],
        data: { permission: InventoryPermissions.DeliveriesRead },
        loadComponent: () =>
          import('./features/deliveries/delivery-list').then((m) => m.DeliveryListPage),
      },
      {
        path: 'deliveries/new',
        canActivate: [permissionGuard],
        data: { permission: InventoryPermissions.DeliveriesWrite },
        loadComponent: () =>
          import('./features/deliveries/delivery-create').then((m) => m.DeliveryCreatePage),
      },
      {
        path: 'deliveries/:streamId',
        canActivate: [permissionGuard],
        data: { permission: InventoryPermissions.DeliveriesRead },
        loadComponent: () =>
          import('./features/deliveries/delivery-detail').then((m) => m.DeliveryDetailPage),
      },
      {
        path: 'trade-partners',
        canActivate: [permissionGuard],
        data: { permission: InventoryPermissions.TradePartnersRead },
        loadComponent: () =>
          import('./features/trade-partners/trade-partners-shell').then(
            (m) => m.TradePartnersShell,
          ),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'list' },
          {
            path: 'list',
            canActivate: [permissionGuard],
            data: { permission: InventoryPermissions.TradePartnersRead },
            loadComponent: () =>
              import('./features/trade-partners/trade-partner-list').then(
                (m) => m.TradePartnerListPage,
              ),
          },
          {
            path: 'list/request',
            canActivate: [permissionGuard],
            data: { permission: InventoryPermissions.TradePartnersWrite },
            loadComponent: () =>
              import('./features/trade-partners/trade-partner-request').then(
                (m) => m.TradePartnerRequestPage,
              ),
          },
          {
            path: 'list/invite',
            canActivate: [permissionGuard],
            data: { permission: InventoryPermissions.TradePartnersWrite },
            loadComponent: () =>
              import('./features/trade-partners/trade-partner-invite').then(
                (m) => m.TradePartnerInvitePage,
              ),
          },
          {
            path: 'list/redeem',
            canActivate: [permissionGuard],
            data: { permission: InventoryPermissions.TradePartnersWrite },
            loadComponent: () =>
              import('./features/trade-partners/trade-partner-redeem').then(
                (m) => m.TradePartnerRedeemPage,
              ),
          },
          {
            path: 'list/:id',
            canActivate: [permissionGuard],
            data: { permission: InventoryPermissions.TradePartnersRead },
            loadComponent: () =>
              import('./features/trade-partners/trade-partner-detail').then(
                (m) => m.TradePartnerDetailPage,
              ),
          },
          {
            path: 'pricing',
            canActivate: [permissionGuard],
            data: { permission: InventoryPermissions.TradePartnersRead },
            loadComponent: () =>
              import('./features/trade-partners/trade-partner-pricing').then(
                (m) => m.TradePartnerPricingPage,
              ),
          },
          {
            path: 'pricing/:id',
            canActivate: [permissionGuard],
            data: { permission: InventoryPermissions.TradePartnersRead },
            loadComponent: () =>
              import('./features/trade-partners/trade-partner-pricing').then(
                (m) => m.TradePartnerPricingPage,
              ),
          },
          {
            path: 'mappings',
            canActivate: [permissionGuard],
            data: { permission: InventoryPermissions.TradePartnersRead },
            loadComponent: () =>
              import('./features/trade-partners/trade-partner-mappings').then(
                (m) => m.TradePartnerMappingsPage,
              ),
          },
          {
            path: 'mappings/:id',
            canActivate: [permissionGuard],
            data: { permission: InventoryPermissions.TradePartnersRead },
            loadComponent: () =>
              import('./features/trade-partners/trade-partner-mappings').then(
                (m) => m.TradePartnerMappingsPage,
              ),
          },
        ],
      },
      {
        path: 'stations',
        canActivate: [permissionGuard],
        data: {
          permissions: [
            InventoryPermissions.DevicesRead,
            InventoryPermissions.ProductContainersRead,
          ],
          permissionMode: 'all',
        },
        loadComponent: () =>
          import('./features/stations/station-menus').then((m) => m.StationMenusPage),
      },
      {
        path: 'stations/:deviceId',
        canActivate: [permissionGuard],
        data: {
          permissions: [
            InventoryPermissions.DevicesRead,
            InventoryPermissions.ProductContainersRead,
          ],
          permissionMode: 'all',
        },
        loadComponent: () =>
          import('./features/stations/station-menus').then((m) => m.StationMenusPage),
      },
      {
        path: 'people',
        canActivate: [permissionGuard],
        data: { permission: InventoryPermissions.OperatorsRead },
        loadComponent: () =>
          import('./features/people/people-list').then((m) => m.PeopleListPage),
      },
      {
        path: 'operators',
        redirectTo: 'people',
        pathMatch: 'full',
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
